import { useState } from "react";
import { HiArrowDown, HiArrowsUpDown, HiArrowUp } from "react-icons/hi2";
import LoadingSpinner from "./LoadingSpinner";


function handleSortFactory(sortConfig, setSortConfig, sortable) {
  return (column) => {
    if (!sortable || !column.sortable) return;
    const key = column.accessor || column.sortKey;
    if (!key) return;
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    } else if (sortConfig.key === key && sortConfig.direction === "desc") {
      direction = null;
    }
    setSortConfig({ key: direction ? key : null, direction });
  };
}

function getSortedDataFactory(sortConfig, data) {
  if (!sortConfig.key || !sortConfig.direction) {
    return data;
  }
  const sortedData = [...data].sort((a, b) => {
    const aValue = sortConfig.key.split(".").reduce((obj, key) => obj?.[key], a);
    const bValue = sortConfig.key.split(".").reduce((obj, key) => obj?.[key], b);
    if (aValue == null && bValue == null) return 0;
    if (aValue == null) return 1;
    if (bValue == null) return -1;
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortConfig.direction === "asc"
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });
  return sortedData;
}

function renderSortIconFactory(sortConfig, sortable) {
  return (column) => {
    const key = column.accessor || column.sortKey;
    if (!sortable || !column.sortable || !key) return null;
    if (sortConfig.key !== key) {
      return <HiArrowsUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
    }
    if (sortConfig.direction === "asc") {
      return <HiArrowUp className="w-4 h-4 ml-1 inline" />;
    }
    return <HiArrowDown className="w-4 h-4 ml-1 inline" />;
  };
}

export default function Table({
  columns = [],
  data = [],
  loading = false,
  emptyMessage = "No data available",
  onRowClick,
  className = "",
  sortable = true,
}) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const handleSort = handleSortFactory(sortConfig, setSortConfig, sortable);
  const renderSortIcon = renderSortIconFactory(sortConfig, sortable);
  const sortedData = getSortedDataFactory(sortConfig, data);

  if (loading) {
    return <LoadingSpinner size="lg" className="py-12" />;
  }

  if (!data || data.length === 0) {
    return (
      <div className="bg-surface shadow rounded-lg border border-border p-12 text-center">
        <p className="text-text opacity-60">{emptyMessage}</p>
      </div>
    );
  }

  return (
    <div
      className={`bg-surface shadow rounded-lg overflow-hidden border border-border ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-border">
          <thead className="bg-background">
            <tr>
              {columns.map((column, index) => {
                const isSortable = sortable && column.sortable;
                return (
                  <th
                    key={index}
                    onClick={() => isSortable && handleSort(column)}
                    className={`px-6 py-3 text-left text-xs font-medium text-text opacity-60 uppercase tracking-wider ${
                      isSortable
                        ? "cursor-pointer hover:opacity-100 transition-opacity select-none"
                        : ""
                    } ${column.headerClassName || ""}`}
                  >
                    <div className="flex items-center">
                      {column.header}
                      {renderSortIcon(column)}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className="bg-surface divide-y divide-border">
            {sortedData.map((row, rowIndex) => (
              <tr
                key={row.id || rowIndex}
                onClick={() => onRowClick && onRowClick(row)}
                className={
                  onRowClick
                    ? "cursor-pointer hover:bg-background transition-colors"
                    : ""
                }
              >
                {columns.map((column, colIndex) => (
                  <td
                    key={colIndex}
                    className={`px-6 py-4 whitespace-nowrap text-sm text-text ${
                      column.cellClassName || ""
                    }`}
                  >
                    {column.render ? column.render(row) : row[column.accessor]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
